<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div class="selection-status-container">
    <PrimeProgressSpinner
      v-if="loading"
      class="spinner"
      strokeWidth="8"
      fill="var(--surface-ground)"
      animationDuration=".5s"
      aria-label="Custom ProgressSpinner"
    />
    <div
      v-if="!loading && pagination && !hasSelectedNodes"
      class="nbrOfSearchResult"
    >
      {{
        $t("howMuchResult", {
          nbrEntries: pagination.count,
          totalEntries: pagination.totalItems,
        })
      }}
    </div>
    <div
      v-else-if="
        hasSelectedNodes &&
        !isCurrentPageFullySelected &&
        !isSelectionRemovalVisible
      "
      class="nbrOfSearchResult"
    >
      {{
        $t("howMuchSelected", {
          nbrEntries: useSelectionStore().nodes.length,
          totalEntries: pagination.totalItems,
        })
      }}
    </div>
    <div
      v-else-if="hasSelectedNodes && isCurrentPageFullySelected"
      class="selectOrDeleteDiv"
    >
      <span class="selection-info">
        {{
          $t("howMuchSelected", {
            nbrEntries: useSelectionStore().nodes.length,
            totalEntries: pagination.totalItems,
          })
        }}
        <button
          v-if="hasMoreElements"
          class="action-link"
          @click="selectAllNodes()"
        >
          {{
            $t("selectAllElementMoreThan", {
              totalEntries: pagination.totalItems,
            })
          }}
        </button>
        <button v-else class="action-link" @click="selectAllNodes()">
          {{ $t("selectAllElement", { totalEntries: pagination.totalItems }) }}
        </button>
      </span>
    </div>
    <div
      v-else-if="hasSelectedNodes && isSelectionRemovalVisible"
      class="selectOrDeleteDiv"
    >
      <span class="selection-info">
        {{
          $t("howMuchSelected", {
            nbrEntries: useSelectionStore().nodes.length,
            totalEntries: pagination.totalItems,
          })
        }}
        <button class="action-link" @click="deleteSelection()">
          {{ $t("deleteSelection") }}
        </button>
      </span>
    </div>
  </div>
</template>

<script>
import { mapState } from "pinia";

import ProgressSpinner from "primevue/progressspinner";
import { useSelectionStore } from "@/stores/selection.js";
import { useNavigationStore } from "@/stores/navigation.js";

export default {
  name: "SelectionStatus",
  components: {
    PrimeProgressSpinner: ProgressSpinner,
  },
  inject: ["actionsService"],
  emits: ["resetPageSelection"],
  computed: {
    useSelectionStore() {
      return useSelectionStore;
    },
    ...mapState(useSelectionStore, [
      "isSelectionRemovalVisible",
      "isCurrentPageFullySelected",
    ]),
    ...mapState(useNavigationStore, [
      "currentNode",
      "loading",
      "pagination",
      "totalRecordNodes",
    ]),
    hasMoreElements() {
      return this.totalRecordNodes >= 500;
    },
    hasSelectedNodes() {
      return (
        useSelectionStore().nodes.length !== 0 &&
        this.pagination &&
        !this.loading
      );
    },
  },
  methods: {
    deleteSelection() {
      useSelectionStore().resetSelection();
      useSelectionStore().setIsCurrentPageFullySelected(false);
      useSelectionStore().setIsSelectionRemovalVisible(false);
      this.$emit("resetPageSelection");
    },
    selectAllNodes() {
      let currentNodeId = this.currentNode.guid || this.currentNode.id;
      this.actionsService
        .selectAllNodes(currentNodeId, true)
        .then((response) => {
          let selectedNodes = response.children;
          if (selectedNodes.length > 0) {
            useSelectionStore().selectNodes(selectedNodes);
          }
          useSelectionStore().setIsCurrentPageFullySelected(false);
          useSelectionStore().setIsSelectionRemovalVisible(true);
        });
    },
  },
};
</script>

<style scoped lang="scss">
.selection-status-container {
  margin-bottom: 0.25rem;
  flex-grow: 1;
  display: flex;
  align-items: center;
}

.spinner {
  width: 1rem;
  height: 1rem;
}

.selection-info {
  margin-left: 0.5rem;
  display: flex;
  align-items: center;
}

.action-link {
  font-weight: bold;
  cursor: pointer;
  margin-left: 0.5rem;
}

.selectOrDeleteDiv {
  flex-grow: 1;
  padding: 1.25rem 1.75rem;
  position: relative;
  border: solid var(--p-primary-color);
  border-width: 0 0 0 0.375rem;
  border-radius: 1rem;
}

.selectOrDeleteDiv::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: var(--p-primary-900);
  opacity: 0.33;
  pointer-events: none;
  border-radius: 0.8rem;
}
</style>
